import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useAllCardsStore, useManageCardNameStore } from '@lib/store';
import type { GetAllCardsResponse } from '@shared/api';
import { Button, Error, Input, Modal } from '@shared/widgets';

interface IForm {
  currentName: string;
  newName: string;
  newNameConfirm: string;
}

interface IChangeNameYourCardModalProps {
  card: GetAllCardsResponse;
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
}

export const ChangeNameYourCardModal = (props: IChangeNameYourCardModalProps) => {
  const allCardsStore = useAllCardsStore();
  const { loading, success, error, create, resetError, clear } = useManageCardNameStore();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
    watch,
    reset,
  } = useForm<IForm>({
    mode: 'onSubmit',
    defaultValues: {
      currentName: props.card?.cardName || '',
      newName: '',
      newNameConfirm: '',
    },
  });
  const newName = watch('newName');
  const currentName = watch('currentName');

  useEffect(() => {
    reset({ currentName: props.card?.cardName || '', newName: '', newNameConfirm: '' });
  }, [props.card]);

  useEffect(() => {
    clearErrors('newNameConfirm');
  }, [newName]);

  useEffect(() => {
    if (loading) return;
    if (error) {
      setError('root', { message: 'Something went wrong. Please try again or contact customer service.' });
      return;
    }
    if (success) {
      allCardsStore.get();
      clear();
      props.onClose();
      props.onConfirm?.();
    }
  }, [loading, error, success]);

  const closeHandler = () => {
    reset();
    resetError();
    props.onClose();
  };
  const changeCardNameHandler = (data: IForm) => create(data, props.card.id);

  return (
    <Modal title='Name Your Card' isOpen={props.isOpen} onClose={closeHandler}>
      <form onSubmit={handleSubmit(changeCardNameHandler)}>
        <div className='flex flex-col gap-4'>
          <Controller
            name='currentName'
            control={control}
            rules={{ required: 'Mandatory field' }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex flex-col gap-2'>
                <Input {...field} label='Current Name' readOnly />
                <Error text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='newName'
            control={control}
            rules={{
              required: 'Mandatory field',
              validate: (value) => (value === currentName ? 'You provided the same card name that this card has now.' : true),
            }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex flex-col gap-2'>
                <Input {...field} label='New Name' maxLength={21} />
                <Error text={error?.message} />
              </div>
            )}
          />
          <Controller
            name='newNameConfirm'
            control={control}
            rules={{
              required: 'Mandatory field',
              validate: (value) => (value !== newName ? "New name doesn't match the retyped one." : true),
            }}
            render={({ field, fieldState: { error } }) => (
              <div className='flex flex-col gap-2'>
                <Input {...field} label='Retype New Name' maxLength={21} />
                <Error text={error?.message} />
              </div>
            )}
          />
          {errors.root?.message && <Error text={errors.root.message} />}
        </div>
        <div className='mt-8 flex gap-4'>
          <Button fullWidth color='outlined' onClick={closeHandler}>
            Cancel
          </Button>
          <Button fullWidth type='submit' isLoading={loading}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
};
